import { useCallback, useMemo, useState, type FormEvent } from 'react';
import {
  basicInfoSections,
  historySections,
  moreSections,
  reportSections,
  stepTitles,
} from '@/mocks/questionnaire';
import type { Answers, FormStatus } from '@/pages/home/types';
import { evaluate } from '@/pages/home/utils/evaluate';
import { buildConsultationRow } from '@/pages/home/utils/buildPayload';
import { supabase } from '@/lib/supabase';
import QuestionnaireNav from '@/pages/home/components/QuestionnaireNav';
import HeroBanner from '@/pages/home/components/HeroBanner';
import StepProgress from '@/pages/home/components/StepProgress';
import WelcomeCard from '@/pages/home/components/WelcomeCard';
import ConfigStep from '@/pages/home/components/ConfigStep';
import SymptomStep from '@/pages/home/components/SymptomStep';
import ResultStep from '@/pages/home/components/ResultStep';

export default function Home() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<Answers>({});
  const [status, setStatus] = useState<FormStatus>({ state: 'idle', message: '' });

  const onAnswer = useCallback((name: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
    setStatus((prev) => (prev.state === 'error' ? { state: 'idle', message: '' } : prev));
  }, []);

  const evaluation = useMemo(() => evaluate(answers), [answers]);

  const goTo = useCallback((next: number) => {
    setStep(Math.min(stepTitles.length, Math.max(1, next)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const reset = useCallback(() => {
    setAnswers({});
    setStatus({ state: 'idle', message: '' });
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();

    if (honeypot) {
      setStatus({ state: 'success', message: '' });
      return;
    }

    setStatus({ state: 'submitting', message: '' });

    try {
      const row = buildConsultationRow(answers, evaluation);

      const { error } = await supabase.from('consultations').insert(row);

      if (error) {
        setStatus({
          state: 'error',
          message: error.message || '提交没有成功,网络可能不太稳,再点一次试试?',
        });
        return;
      }

      setStatus({
        state: 'success',
        message: '郡主收到你的表了,会尽快回复你。',
      });
    } catch {
      setStatus({
        state: 'error',
        message: '提交没有成功,网络可能不太稳,再点一次试试?',
      });
    }
  };

  const panelClass = (target: number) => (step === target ? 'space-y-4' : 'hidden');

  return (
    <div className="min-h-screen bg-background-100 pb-10 relative">
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-primary-200/30 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-accent-200/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[380px] h-[380px] rounded-full bg-primary-100/50 blur-3xl" />
      </div>

      <div className="relative">
        <QuestionnaireNav onReset={reset} onPrev={() => goTo(step - 1)} canPrev={step > 1} />
        <HeroBanner />

        <div className="max-w-xl mx-auto px-4 mt-4">
          <StepProgress step={step} onJump={goTo} />
        </div>

        <main className="max-w-xl mx-auto px-4 mt-4">
          <form
            id="menopause-check-form"
            noValidate
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className={panelClass(1)}>
              <WelcomeCard />
              <ConfigStep sections={basicInfoSections} answers={answers} onAnswer={onAnswer} />
            </div>

            <div className={panelClass(2)}>
              <ConfigStep sections={reportSections} answers={answers} onAnswer={onAnswer} />
              <div className="bg-background-50 rounded-xl border border-secondary-200/70 px-4 py-3">
                <p className="text-xs text-foreground-500 leading-relaxed flex gap-2">
                  <i className="ri-file-list-3-line text-base text-primary-400 shrink-0"></i>
                  <span>
                    报告单上的数字不用完全准确,大概范围也可以。如果没有做过检查,直接点「下一步」就好,郡主会告诉你该查哪些。
                  </span>
                </p>
              </div>
            </div>

            <div className={panelClass(3)}>
              <ConfigStep sections={historySections} answers={answers} onAnswer={onAnswer} />
            </div>

            <div className={panelClass(4)}>
              <div className="bg-background-50 rounded-xl border border-primary-200/50 p-4">
                <p className="text-sm text-primary-600 font-medium mb-1">按最近两周的真实感受勾选</p>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  没有的选「无」,偶尔有一点选「轻」,经常出现选「中」,影响到日常生活就选「重」。
                </p>
              </div>
              <SymptomStep
                answers={answers}
                onAnswer={onAnswer}
                totalScore={evaluation.totalScore}
                ratedCount={evaluation.ratedCount}
              />
            </div>

            <div className={panelClass(5)}>
              <ConfigStep sections={moreSections} answers={answers} onAnswer={onAnswer} />
              <div className="bg-primary-50 rounded-xl border border-primary-200/60 px-4 py-3">
                <p className="text-xs text-primary-700 leading-relaxed flex gap-2">
                  <i className="ri-shield-check-line text-base shrink-0"></i>
                  <span>你填的内容只会给郡主本人看到,不会公开,也不会用来推销任何产品。</span>
                </p>
              </div>
            </div>

            <div className={panelClass(6)}>
              <ResultStep
                answers={answers}
                evaluation={evaluation}
                status={status}
                onRestart={reset}
              />
            </div>

            <div className="hp-field">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {step < stepTitles.length ? (
              <div className="flex gap-3 pt-2">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={() => goTo(step - 1)}
                    className="px-5 py-3.5 rounded-xl text-base font-semibold text-foreground-600 bg-background-50 border border-secondary-200 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    上一步
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => goTo(step + 1)}
                  className="py-3.5 rounded-xl text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1 flex-1"
                >
                  下一步
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ) : (
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={status.state === 'submitting' || status.state === 'success'}
                  className="w-full py-3.5 rounded-xl text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1"
                >
                  {status.state === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      正在提交
                    </>
                  ) : status.state === 'success' ? (
                    <>
                      <i className="ri-checkbox-circle-fill"></i>
                      已经提交
                    </>
                  ) : (
                    <>
                      提交给郡主
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
                <p className="text-[11px] text-foreground-400 text-center mt-2">
                  {status.state === 'success'
                    ? '已存档,郡主会在后台看到你的完整记录'
                    : '提交后郡主会看到你填的全部内容'}
                </p>
              </div>
            )}

            <p className="text-xs text-foreground-400 text-center pt-4">
              本工具提供营养保健建议,不替代医疗诊断 · 李郡主更年期小屋
            </p>
          </form>
        </main>
      </div>
    </div>
  );
}